import React, { useState, useCallback, useEffect } from "react";
import { Button, Chip, Input, Spinner } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { siteConfig } from "@/config/site";
import { logger } from "@/telemetry";

interface ServiceCredentialSectionProps {
  integrationId: string;
  integrationName: string;
  credentialType: string;
  setupGuide?: string;
  isAdmin: boolean;
  serviceOperations: string[];
}

interface CredentialStatus {
  integration_type: string;
  credential_type: string;
  has_credential: boolean;
  is_active: boolean;
  created_by_email?: string;
  created_at?: string;
  rotated_at?: string;
  provider_identity?: string;
  provider_site?: string;
  enabled_operations: string[];
}

export const ServiceCredentialSection: React.FC<ServiceCredentialSectionProps> = ({
  integrationId,
  integrationName,
  credentialType,
  setupGuide,
  isAdmin,
  serviceOperations,
}) => {
  const { t } = useTranslation();
  const { authenticatedFetch } = useWebuildAPIClient();
  const baseUrl = `${siteConfig().adminAPIURL.replace(/\/$/, "")}/admin/v1`;

  const [status, setStatus] = useState<CredentialStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [credentialValue, setCredentialValue] = useState("");
  const [providerIdentity, setProviderIdentity] = useState("");
  const [providerSite, setProviderSite] = useState("");
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!authenticatedFetch) return;

    try {
      setIsLoading(true);
      const response = await authenticatedFetch(
        `${baseUrl}/integrations/${integrationId}/service-credential/status`,
      );

      if (response.ok) {
        const data = await response.json();

        setStatus(data);
      }
    } catch (err) {
      logger.error("Failed to fetch credential status", { error: String(err) });
    } finally {
      setIsLoading(false);
    }
  }, [authenticatedFetch, baseUrl, integrationId]);

  useEffect(() => {
    if (isAdmin) {
      fetchStatus();
    } else {
      setIsLoading(false);
    }
  }, [isAdmin, fetchStatus]);

  const handleSave = async () => {
    if (!authenticatedFetch || !credentialValue.trim()) return;
    setIsSaving(true);
    setError(null);

    try {
      const response = await authenticatedFetch(
        `${baseUrl}/integrations/${integrationId}/service-credential`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            credential: credentialValue,
            credential_type: credentialType,
            provider_identity: providerIdentity || undefined,
            provider_site: providerSite || undefined,
            enabled_operations: serviceOperations,
          }),
        },
      );

      if (response.ok) {
        const data = await response.json();

        setStatus(data);
        setShowForm(false);
        setCredentialValue("");
      } else {
        const errData = await response.json();

        setError(errData.detail || t("failedToSaveCredential"));
      }
    } catch (err) {
      logger.error("Failed to save credential", { error: String(err) });
      setError(t("failedToSaveCredential"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!authenticatedFetch) return;
    setIsDeleting(true);
    setError(null);

    try {
      const response = await authenticatedFetch(
        `${baseUrl}/integrations/${integrationId}/service-credential`,
        { method: "DELETE" },
      );

      if (response.ok) {
        setStatus(null);
        setShowForm(false);
      }
    } catch (err) {
      logger.error("Failed to delete credential", { error: String(err) });
      setError(t("failedToDeleteCredential"));
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Spinner size="sm" />
      </div>
    );
  }

  // Non-admin view
  if (!isAdmin) {
    return (
      <div className="mt-3 p-3 bg-default-50 rounded-lg">
        <div className="flex items-center gap-2">
          <Icon className="text-success" icon="solar:shield-check-bold" width={20} />
          <span className="text-sm text-default-600">{t("serviceCredentialManagedByAdmin")}</span>
        </div>
        {serviceOperations.length > 0 && (
          <div className="mt-2">
            <span className="text-xs text-default-400">
              {t("adminOperations")} {serviceOperations.join(", ")}
            </span>
          </div>
        )}
      </div>
    );
  }

  // Admin view
  return (
    <div className="mt-3 p-3 bg-default-50 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="text-warning" icon="solar:key-bold" width={20} />
          <span className="text-sm font-medium">{t("serviceCredential")}</span>
        </div>
        {status?.has_credential && (
          <Chip color="success" size="sm" variant="flat">
            {t("encrypted")}
          </Chip>
        )}
      </div>

      {status?.has_credential ? (
        <>
          <div className="text-xs text-default-400 space-y-1">
            {status.created_by_email && (
              <p>
                {t("createdBy")} {status.created_by_email}
              </p>
            )}
            {status.created_at && (
              <p>
                {t("created")} {new Date(status.created_at).toLocaleDateString()}
              </p>
            )}
            {status.rotated_at && (
              <p>
                {t("lastRotated")} {new Date(status.rotated_at).toLocaleDateString()}
              </p>
            )}
            {status.provider_identity && (
              <p>
                {t("providerIdentity")} {status.provider_identity}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              color="warning"
              isDisabled={showForm}
              size="sm"
              variant="flat"
              onPress={() => setShowForm(true)}
            >
              {t("rotateCredential")}
            </Button>
            <Button
              color="danger"
              isLoading={isDeleting}
              size="sm"
              variant="flat"
              onPress={handleDelete}
            >
              {t("Delete")}
            </Button>
          </div>
        </>
      ) : (
        <Button
          color="primary"
          isDisabled={showForm}
          size="sm"
          variant="flat"
          onPress={() => setShowForm(true)}
        >
          {t("addServiceCredential")}
        </Button>
      )}

      {showForm && (
        <div className="space-y-3 pt-2 border-t border-default-200">
          {setupGuide && (
            <div className="text-xs text-default-400 whitespace-pre-line bg-default-100 p-2 rounded">
              {setupGuide}
            </div>
          )}
          <Input
            label={`${integrationName} ${credentialType.replace("_", " ")}`}
            placeholder={t("pasteCredentialHere")}
            size="sm"
            type="password"
            value={credentialValue}
            onValueChange={setCredentialValue}
          />
          {integrationId === "jira" && (
            <>
              <Input
                label={t("adminEmail")}
                placeholder="admin@company.com"
                size="sm"
                value={providerIdentity}
                onValueChange={setProviderIdentity}
              />
              <Input
                label={t("jiraSiteUrl")}
                placeholder="https://company.atlassian.net"
                size="sm"
                value={providerSite}
                onValueChange={setProviderSite}
              />
            </>
          )}
          {error && <p className="text-xs text-danger">{error}</p>}
          <div className="flex gap-2">
            <Button
              color="primary"
              isDisabled={!credentialValue.trim()}
              isLoading={isSaving}
              size="sm"
              onPress={handleSave}
            >
              {t("Save")}
            </Button>
            <Button
              size="sm"
              variant="flat"
              onPress={() => {
                setShowForm(false);
                setCredentialValue("");
                setError(null);
              }}
            >
              {t("Cancel")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
